import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { usePageHeaderStore } from '../../stores/pageHeader';
import { useAuthStore } from '../../stores/auth';
import {
  FolderKanban,
  Layers,
  FileText,
  Plus,
  ChevronRight,
  ArrowLeft,
  Activity,
  Book,
  Globe,
  X,
  AlertCircle,
  Edit3,
  Calendar,
  UserPlus,
  Send,
  Mail
} from 'lucide-react';
import { useEffect, useState } from 'react';

interface DomainPattern {
  pattern: string;
  label?: string;
  isPrimary?: boolean;
}

interface ProjectSettings {
  allowedDomains?: DomainPattern[];
}

interface ProjectDetail {
  id: string;
  orgId: string;
  orgName: string;
  name: string;
  key: string;
  description: string | null;
  status: string;
  settings?: ProjectSettings;
  epicCount: number;
  requestCount: number;
  memberCount: number;
  createdAt: string;
  updatedAt: string;
}

interface Epic {
  id: string;
  title: string;
  description: string | null;
  status: string;
  isSystem: boolean;
  requestCount: number;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

// ============================================
// Invite Member Modal
// ============================================

function InviteMemberModal({
  projectId,
  projectName,
  onClose,
  onSuccess,
}: {
  projectId: string;
  projectName: string;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('viewer');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const inviteMutation = useMutation({
    mutationFn: async () => {
      return api.post('/invitations', {
        projectId,
        email,
        role,
        message: message || undefined,
      });
    },
    onSuccess: () => {
      setSuccess(true);
      setEmail('');
      setMessage('');
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 1500);
    },
    onError: (err: any) => {
      setError(err.message || 'Failed to send invitation');
    },
  });

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="card w-full max-w-md animate-fade-in">
        <div className="card-header justify-between">
          <h3 className="card-title flex items-center gap-2">
            <UserPlus className="w-5 h-5" style={{ color: 'var(--primary)' }} />
            Invite Member
          </h3>
          <button onClick={onClose} className="btn btn-ghost p-1">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {success ? (
          <div className="py-8 text-center">
            <div className="w-12 h-12 rounded-full bg-success-50 dark:bg-success-900/30 flex items-center justify-center mx-auto mb-4">
              <Send className="w-6 h-6 text-success-600 dark:text-success-400" />
            </div>
            <p className="text-lg font-medium text-neutral-900 dark:text-neutral-100">Invitation Sent!</p>
            <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1">
              An email has been sent to {email}
            </p>
          </div>
        ) : (
          <>
            <div className="space-y-4">
              <div className="p-3 bg-neutral-50 dark:bg-neutral-900/50 rounded-lg border border-neutral-200 dark:border-neutral-700">
                <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-1">Inviting to project</p>
                <p className="font-medium text-neutral-900 dark:text-neutral-100">{projectName}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-default mb-1">
                  <Mail className="w-4 h-4 inline mr-1" />
                  Email Address
                </label>
                <input
                  type="email"
                  className="input w-full"
                  placeholder="colleague@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-default mb-1">
                  Role
                </label>
                <select
                  className="input w-full"
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                >
                  <option value="viewer">Viewer — can view backoffice & use extension</option>
                  <option value="editor">Editor — can edit stories and manage content</option>
                  <option value="admin">Admin — full project access</option>
                </select>
                <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                  After registration, the user will have access to this project
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-default mb-1">
                  Personal Message <span className="text-muted font-normal">(optional)</span>
                </label>
                <textarea
                  className="input w-full resize-none"
                  rows={2}
                  placeholder="Add a personal note to the invitation..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                />
              </div>
              
              {error && (
                <div className="flex items-center gap-2 p-3 rounded-md bg-error-50 text-error-600">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <p className="text-sm">{error}</p>
                </div>
              )}
            </div>
            
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={onClose} className="btn btn-secondary">
                Cancel
              </button>
              <button
                onClick={() => inviteMutation.mutate()}
                disabled={inviteMutation.isPending || !email.trim() || !email.includes('@')}
                className="btn btn-primary"
              >
                {inviteMutation.isPending ? (
                  <>Sending...</>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Send Invitation
                  </>
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ============================================
// New Epic Modal
// ============================================

function NewEpicModal({
  projectId,
  onClose,
  onSuccess,
}: {
  projectId: string;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState('');

  const createMutation = useMutation({
    mutationFn: async () => {
      return api.post('/epics', {
        projectId,
        title,
        description: description || undefined,
      });
    },
    onSuccess: () => {
      onSuccess();
      onClose();
    },
    onError: (err: any) => {
      setError(err.message || 'Failed to create epic');
    },
  });

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="card w-full max-w-lg animate-fade-in">
        <div className="card-header justify-between">
          <h3 className="card-title flex items-center gap-2">
            <Plus className="w-5 h-5" style={{ color: 'var(--primary)' }} />
            New Epic
          </h3>
          <button onClick={onClose} className="btn btn-ghost p-1">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-default mb-1">
              Epic Title
            </label>
            <input
              type="text"
              className="input w-full"
              placeholder="e.g., User Authentication"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-default mb-1">
              Description <span className="text-muted font-normal">(optional)</span>
            </label>
            <textarea
              className="input w-full resize-none"
              rows={3}
              placeholder="Brief description of the epic..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          
          {error && (
            <div className="flex items-center gap-2 p-3 rounded-md bg-danger/10 text-danger">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <p className="text-sm">{error}</p>
            </div>
          )}
        </div>
        
        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onClose} className="btn btn-secondary">
            Cancel
          </button>
          <button
            onClick={() => createMutation.mutate()}
            disabled={createMutation.isPending || !title.trim()}
            className="btn btn-primary"
          >
            {createMutation.isPending ? 'Creating...' : 'Create Epic'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================
// Edit Project Modal
// ============================================

function EditProjectModal({
  project,
  onClose,
  onSuccess,
}: {
  project: ProjectDetail;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [name, setName] = useState(project.name);
  const [description, setDescription] = useState(project.description || '');
  const [status, setStatus] = useState(project.status);
  const [error, setError] = useState('');

  const updateMutation = useMutation({
    mutationFn: async () => {
      return api.patch(`/projects/${project.id}`, {
        name,
        description: description || null,
        status,
      });
    },
    onSuccess: () => {
      onSuccess();
      onClose();
    },
    onError: (err: any) => {
      setError(err.message || 'Failed to update project');
    },
  });

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="card w-full max-w-lg animate-fade-in">
        <div className="card-header justify-between">
          <h3 className="card-title flex items-center gap-2">
            <Edit3 className="w-5 h-5" style={{ color: 'var(--primary)' }} />
            Edit Project
          </h3>
          <button onClick={onClose} className="btn btn-ghost p-1">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-default mb-1">
              Project Name
            </label>
            <input
              type="text"
              className="input w-full"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-default mb-1">
              Project Key
            </label>
            <input
              type="text"
              className="input w-full font-mono"
              value={project.key}
              disabled
            />
            <p className="text-xs text-muted mt-1">
              Project key cannot be changed
            </p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-default mb-1">
              Status
            </label>
            <select
              className="input w-full"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="active">Active</option>
              <option value="archived">Archived</option>
              <option value="paused">Paused</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-default mb-1">
              Description <span className="text-muted font-normal">(optional)</span>
            </label>
            <textarea
              className="input w-full resize-none"
              rows={3}
              placeholder="Brief description of the project..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          
          {error && (
            <div className="flex items-center gap-2 p-3 rounded-md bg-danger/10 text-danger">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <p className="text-sm">{error}</p>
            </div>
          )}
        </div>
        
        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onClose} className="btn btn-secondary">
            Cancel
          </button>
          <button
            onClick={() => updateMutation.mutate()}
            disabled={updateMutation.isPending || !name.trim()}
            className="btn btn-primary"
          >
            {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================
// Main Component
// ============================================

export function ProjectDetailPage() {
  const { orgId, projectId } = useParams<{ orgId: string; projectId: string }>();
  const { setHeader, clearHeader } = usePageHeaderStore();
  const { user } = useAuthStore();
  const [showEditModal, setShowEditModal] = useState(false);
  const [showNewEpicModal, setShowNewEpicModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const queryClient = useQueryClient();
  
  const { data: project, isLoading } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => api.get<ProjectDetail>(`/projects/${projectId}`),
    enabled: !!projectId,
  });
  
  const { data: epics } = useQuery({
    queryKey: ['project-epics', projectId],
    queryFn: () => api.get<{ data: Epic[] }>(`/epics?projectId=${projectId}`),
    enabled: !!projectId,
  });
  
  // Set header when project data is loaded (must be before any early returns!)
  useEffect(() => {
    if (project) {
      const actions = [
        {
          id: 'add-member',
          label: 'Invite',
          icon: <UserPlus className="w-4 h-4" />,
          onClick: () => setShowInviteModal(true),
          variant: 'primary' as const,
        },
        {
          id: 'edit',
          label: 'Edit',
          icon: <Edit3 className="w-4 h-4" />,
          onClick: () => setShowEditModal(true),
          variant: 'secondary' as const,
        },
        {
          id: 'domains',
          label: 'Domains',
          icon: <Globe className="w-4 h-4" />,
          href: `/app/organizations/${orgId}/projects/${projectId}/domains`,
          variant: 'secondary' as const,
        },
        {
          id: 'knowledge',
          label: 'KB',
          icon: <Book className="w-4 h-4" />,
          href: `/app/organizations/${orgId}/projects/${projectId}/knowledge`,
          variant: 'secondary' as const,
        },
      ];
      
      // Only show telemetry for superadmins
      if (user?.isSuperAdmin) {
        actions.push({
          id: 'telemetry',
          label: 'Logs',
          icon: <Activity className="w-4 h-4" />,
          href: `/app/organizations/${orgId}/projects/${projectId}/telemetry`,
          variant: 'secondary' as const,
        });
      }
      
      setHeader(
        project.name,
        project.key,
        <FolderKanban className="w-5 h-5" style={{ color: 'var(--primary)' }} />,
        <span className={`badge ${project.status === 'active' ? 'badge-success' : 'badge-neutral'}`}>
          {project.status}
        </span>,
        { actions }
      );
    }
    return () => clearHeader();
  }, [project, orgId, projectId, user, setHeader, clearHeader]);
  
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="skeleton h-8 w-1/4" />
        <div className="skeleton h-48 rounded-card" />
      </div>
    );
  }
  
  if (!project) {
    return (
      <div className="empty-state">
        <FolderKanban className="empty-state-icon" />
        <p className="empty-state-title">Project not found</p>
        <p className="empty-state-text">The project you're looking for doesn't exist</p>
        <Link to={`/app/organizations/${orgId}`} className="btn btn-secondary">
          <ArrowLeft className="w-4 h-4" />
          Back to Organization
        </Link>
      </div>
    );
  }
  
  return (
    <div className="animate-fade-in">
      {/* Epics */}
      <div className="card">
        <div className="card-header justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-md flex items-center justify-center"
              style={{ background: 'var(--primary-dim)' }}
            >
              <Layers className="w-4 h-4" style={{ color: 'var(--primary)' }} />
            </div>
            <h3 className="card-title">Epics</h3>
          </div>
          <button className="btn btn-secondary text-sm" onClick={() => setShowNewEpicModal(true)}>
            <Plus className="w-4 h-4" />
            New Epic
          </button>
        </div>
        
        <div className="divide-y" style={{ borderColor: 'var(--border)' }}>
          {epics?.data.map((epic, index) => (
            <Link
              key={epic.id}
              to={`/app/organizations/${orgId}/projects/${projectId}/epics/${epic.id}`}
              className="block py-4 hover:bg-surface-2 -mx-5 px-5 transition-all rounded-md group"
              style={{ animationDelay: `${index * 50}ms` }}
            >
              <div className="flex items-start gap-3">
                <Layers className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: 'var(--primary)' }} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-medium text-default group-hover:text-primary transition-colors truncate">
                      {epic.title}
                    </p>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {epic.isSystem && (
                        <span className="badge badge-neutral hidden sm:inline-flex">System</span>
                      )}
                      <span className={`badge status-${epic.status.toLowerCase().replace('_', '-')}`}>
                        {epic.status.replace('_', ' ')}
                      </span>
                      <ChevronRight className="w-5 h-5 text-subtle group-hover:text-primary group-hover:translate-x-1 transition-all" />
                    </div>
                  </div>
                  {epic.description && (
                    <p className="text-sm text-muted line-clamp-1 mt-1">{epic.description}</p>
                  )}
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-xs text-muted">
                    <span className="flex items-center gap-1">
                      <FileText className="w-3 h-3" />
                      {epic.requestCount} {epic.requestCount === 1 ? 'story' : 'stories'}
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {new Date(epic.updatedAt).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              </div>
            </Link>
          ))}
          {(!epics?.data || epics.data.length === 0) && (
            <div className="py-8 text-center">
              <Layers className="w-10 h-10 mx-auto mb-3 text-subtle" />
              <p className="text-muted">No epics yet</p>
              <button className="btn btn-primary mt-4 text-sm" onClick={() => setShowNewEpicModal(true)}>
                <Plus className="w-4 h-4" />
                Create First Epic
              </button>
            </div>
          )}
        </div>
      </div>
      
      {/* Edit Project Modal */}
      {showEditModal && project && (
        <EditProjectModal
          project={project}
          onClose={() => setShowEditModal(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['project', projectId] });
          }}
        />
      )}
      
      {/* New Epic Modal */}
      {showNewEpicModal && projectId && (
        <NewEpicModal
          projectId={projectId}
          onClose={() => setShowNewEpicModal(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['project', projectId] });
            queryClient.invalidateQueries({ queryKey: ['project-epics', projectId] });
          }}
        />
      )}
      
      {/* Invite Member Modal */}
      {showInviteModal && project && (
        <InviteMemberModal
          projectId={project.id}
          projectName={project.name}
          onClose={() => setShowInviteModal(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['project', projectId] });
          }}
        />
      )}
    </div>
  );
}
